// SPDX-License-Identifier: LicenseRef-Proprietary
//
// Frontend-side catalog of background utility (healthy-state HRQoL) registry
// sources. Mirrors the backend JSON files under
// R/domain/decision_tree/multi_year/data/background_utility_sources/
// closely enough for UI labeling and audit display.
//
// Keep IDs in sync with the backend filenames (id === <file basename>).

export type BackgroundUtilityValueType = 'utility_weight';

export interface BackgroundUtilitySourceMeta {
  id: string;
  label: string;
  valueType: BackgroundUtilityValueType;
  ageRange: readonly [number, number];
  // Compact, user-facing summary of the source's age-band values. Exact per-integer
  // table lives in the backend JSON and is what the resolver consults.
  summary: string;
  note?: string;
}

export const BACKGROUND_UTILITY_SOURCES: readonly BackgroundUtilitySourceMeta[] = [
  {
    id: 'terveys_2011_finland',
    label: 'Terveys 2011 (Finnish EQ-5D, VAS-preference weighted)',
    valueType: 'utility_weight',
    ageRange: [30, 99],
    summary:
      'Anchors: 30: 0.88 · 45: 0.83 · 55: 0.81 · 65: 0.79 · 75+: 0.68 (linearly interpolated between anchors; flat at 0.68 for ages 75+).',
    note:
      'Population HRQoL weights from THL Terveys 2011, sexes averaged. Applied as a per-cycle multiplier on healthy-state QALYs in both arms. Incremental effect on ICER is small but non-zero and grows with time horizon.',
  },
];

export const BACKGROUND_UTILITY_SOURCE_OPTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'none', label: 'None (per-cycle utility = 1.0; current default)' },
  ...BACKGROUND_UTILITY_SOURCES.map((s) => ({ id: s.id, label: s.label })),
];

export const findBackgroundUtilitySource = (
  id: string | undefined,
): BackgroundUtilitySourceMeta | undefined =>
  id && id !== 'none' ? BACKGROUND_UTILITY_SOURCES.find((s) => s.id === id) : undefined;
